import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Activity, TrendingUp } from 'lucide-react';
import { trackingAPI } from '../lib/api';
import { LoadingSpinner } from './ui/LoadingSpinner';

interface SetLog {
  id: string;
  setNumber: number;
  weightKg: number | null;
  reps: number | null;
  rpe: number | null;
  completedAt: string;
  exerciseId: string;
  exerciseName: string;
  planName: string;
  weekNumber: number;
  dayNumber: number;
}

/** 1RM estimado (Epley). Necesita peso y reps. */
function epley1RM(weightKg: number | null, reps: number | null): number | null {
  if (!weightKg || !reps || reps < 1) return null;
  return Math.round(weightKg * (1 + reps / 30));
}

function fmtDate(iso: string): string {
  const norm = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(iso) ? iso.replace(' ', 'T') + 'Z' : iso;
  const d = new Date(norm);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('es-AR', { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * Progresión de rendimiento del alumno: series reales (peso/reps/RPE) que cargó,
 * agrupadas por ejercicio y sesión. Solo muestra datos de los planes del entrenador.
 */
export function PerformanceHistory({ studentId }: { studentId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['tracking-history', studentId],
    queryFn: () => trackingAPI.getHistory(studentId) as Promise<SetLog[]>,
  });

  const [exerciseId, setExerciseId] = useState<string>('');

  // Ejercicios que tienen series registradas.
  const exercises = useMemo(() => {
    const map = new Map<string, string>();
    for (const log of data ?? []) map.set(log.exerciseId, log.exerciseName);
    return [...map.entries()].map(([id, name]) => ({ id, name }));
  }, [data]);

  const selected = exerciseId || exercises[0]?.id || '';

  // Sesiones (agrupadas por fecha + día del plan) del ejercicio elegido, recientes primero.
  const sessions = useMemo(() => {
    const logs = (data ?? []).filter((l) => l.exerciseId === selected);
    const groups = new Map<string, SetLog[]>();
    for (const l of logs) {
      const key = `${l.completedAt.slice(0, 10)}·${l.planName}·${l.weekNumber}·${l.dayNumber}`;
      const arr = groups.get(key) ?? [];
      arr.push(l);
      groups.set(key, arr);
    }
    return [...groups.values()]
      .map((sets) => sets.sort((a, b) => a.setNumber - b.setNumber))
      .sort((a, b) => (a[0].completedAt < b[0].completedAt ? 1 : -1));
  }, [data, selected]);

  if (isLoading) {
    return (
      <div className="card flex items-center justify-center py-8">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="card">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <h2 className="text-lg font-semibold text-dark-50 flex items-center gap-2">
          <Activity className="w-5 h-5 text-dark-300" />
          Rendimiento registrado
        </h2>
        {exercises.length > 0 && (
          <select
            value={selected}
            onChange={(e) => setExerciseId(e.target.value)}
            className="input w-auto"
          >
            {exercises.map((ex) => (
              <option key={ex.id} value={ex.id}>
                {ex.name}
              </option>
            ))}
          </select>
        )}
      </div>

      {exercises.length === 0 ? (
        <p className="text-dark-400 text-sm py-6 text-center">
          Todavía no hay series registradas. Cuando el alumno cargue peso/reps/RPE al entrenar, la
          progresión aparece acá.
        </p>
      ) : (
        <div className="space-y-4">
          {sessions.map((sets, i) => {
            const s0 = sets[0];
            const best1RM = Math.max(0, ...sets.map((s) => epley1RM(s.weightKg, s.reps) ?? 0));
            return (
              <div key={i} className="p-3 rounded-xl bg-dark-800/50">
                <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                  <p className="text-sm text-dark-200">
                    {fmtDate(s0.completedAt)}
                    <span className="text-dark-500">
                      {'  ·  '}
                      {s0.planName} · Sem {s0.weekNumber} · Día {s0.dayNumber}
                    </span>
                  </p>
                  {best1RM > 0 && (
                    <span className="badge badge-neutral inline-flex items-center gap-1 text-xs">
                      <TrendingUp className="w-3 h-3" />
                      1RM~{best1RM} kg
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {sets.map((s) => (
                    <span
                      key={s.id}
                      className="font-mono text-sm text-dark-200 px-2 py-1 rounded-lg bg-dark-900/60"
                    >
                      {s.weightKg != null ? `${s.weightKg}kg` : '—'}
                      {s.reps != null ? ` × ${s.reps}` : ''}
                      {s.rpe != null ? `  RPE ${s.rpe}` : ''}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
